import { ApiError } from "@/core/error";

interface FetchApiParams {
  errorMessage?: string;
}

export function getGearboxApiUrl(): string {
  const baseUrl = import.meta.env.VITE_API_URL;
  if (!baseUrl) {
    throw new ApiError("VITE_API_URL is not set", 500);
  }
  return baseUrl;
}

/**
 * Fetches a single endpoint and returns the JSON response
 * Throws ApiError if the request fails
 */
export async function fetchGearboxApi<T = unknown>(
  endpoint: string,
  params: FetchApiParams = {
    errorMessage: "Failed to fetch data",
  },
): Promise<T> {
  const baseUrl = getGearboxApiUrl();
  const response = await fetch(`${baseUrl}${endpoint}`, {
    method: "GET",
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message ||
        `${params.errorMessage}: ${response.status} ${response.statusText}`,
      response.status,
    );
  }

  return response.json();
}

/**
 * Fetches multiple endpoints in parallel and returns the JSON responses
 * Throws ApiError if any request fails
 */
export async function fetchGearboxApis<T extends unknown[]>(
  endpoints: string[],
  params: FetchApiParams = {
    errorMessage: "Failed to fetch data",
  },
): Promise<T> {
  const baseUrl = getGearboxApiUrl();
  const responses = await Promise.all(
    endpoints.map(endpoint =>
      fetch(`${baseUrl}${endpoint}`, {
        method: "GET",
      }),
    ),
  );

  if (responses.some(response => !response.ok)) {
    const failedResponse = responses.find(({ ok }) => !ok)!;
    const errorData = await failedResponse.json().catch(() => ({}));

    throw new ApiError(
      errorData.message ||
        `${params.errorMessage}: ${failedResponse.status} ${failedResponse.statusText}`,
      failedResponse.status,
    );
  }

  return Promise.all(responses.map(response => response.json())) as Promise<T>;
}
